/**
 * alma-tracking-capture.js
 * Phase B — multi-form support + click_id (SHOP-12 fix ROLLED BACK 2026-05-05)
 *
 * Captures gclid, wbraid, gbraid, fbclid, dclid, msclkid and UTM params
 * from the URL on every page load and persists them to first-party cookies
 * (90-day expiry).
 *
 * On every page, finds ALL forms that have a contact[gclid] hidden field,
 * populates all attribution hidden fields from cookies, and attaches a
 * submit listener that fires the appropriate GA4 event:
 *
 *   - General contact form (no contact[product] hidden field):
 *       fires 'form_submit' with value: 500
 *   - Product inquiry form (id contains 'product-request' OR has
 *     contact[product] hidden field OR class contains 'product-request-form'):
 *       fires 'product_inquiry' with value: 2500, plus product_title + sku
 *
 * Both events also send gclid value under both 'gclid' (native Ads
 * attribution) AND 'click_id' (registered GA4 custom dimension).
 *
 * NOTE: SHOP-12 (overcounting from submit-attempt-instead-of-submit-success
 * firing) is OPEN. The 2026-05-05 attempt to gate firing on a post-redirect
 * success-page render did not produce events in GA4 Realtime; rolled back
 * pending further diagnosis. This file fires on the submit event — meaning
 * submissions blocked at the POST layer (hCaptcha / network drops / server
 * validation rejects) still register a phantom GA4 event. Real-user volume
 * impact is small but non-zero.
 *
 * Deploy to: assets/alma-tracking-capture.js
 * Loaded from layout/theme.liquid before </body>:
 *   <script src="{{ 'alma-tracking-capture.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  var COOKIE_PREFIX = 'alma_attr_';
  var EXPIRY_DAYS = 90;
  var PARAMS = [
    'gclid', 'wbraid', 'gbraid', 'fbclid', 'dclid', 'msclkid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'
  ];
  var FORM_DESTINATION = 'alex@almaaudio.com';
  var CONTACT_FORM_VALUE = 500;
  var PRODUCT_INQUIRY_VALUE = 2500;

  // ── Helpers ────────────────────────────────────────────────────────────────

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function getCookie(name) {
    var match = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return match ? decodeURIComponent(match[1]) : null;
  }

  // ── Step 1: Capture URL params → cookies ──────────────────────────────────

  var urlParams = new URLSearchParams(window.location.search);
  var captured = {};

  PARAMS.forEach(function (param) {
    var val = urlParams.get(param);
    if (val) {
      setCookie(COOKIE_PREFIX + param, val, EXPIRY_DAYS);
      captured[param] = val;
    }
  });

  // First-touch capture: never overwrite landing_page / referrer_url
  if (!getCookie(COOKIE_PREFIX + 'landing_page')) {
    setCookie(COOKIE_PREFIX + 'landing_page', window.location.href, EXPIRY_DAYS);
  }
  if (!getCookie(COOKIE_PREFIX + 'referrer_url') && document.referrer) {
    setCookie(COOKIE_PREFIX + 'referrer_url', document.referrer, EXPIRY_DAYS);
  }

  // ── Step 2: Push attribution_capture event to dataLayer ───────────────────

  window.dataLayer = window.dataLayer || [];
  if (Object.keys(captured).length > 0) {
    window.dataLayer.push({
      event: 'attribution_capture',
      alma_gclid: captured.gclid || getCookie(COOKIE_PREFIX + 'gclid') || '',
      alma_utm_source: captured.utm_source || getCookie(COOKIE_PREFIX + 'utm_source') || '',
      alma_utm_medium: captured.utm_medium || getCookie(COOKIE_PREFIX + 'utm_medium') || '',
      alma_utm_campaign: captured.utm_campaign || getCookie(COOKIE_PREFIX + 'utm_campaign') || ''
    });
  }

  // ── Step 3: Find ALL attribution-aware forms; populate + attach listeners ─

  function isProductInquiryForm(form) {
    if (form.id && form.id.indexOf('product-request') !== -1) return true;
    if (form.querySelector('input[name="contact[product]"]')) return true;
    if (typeof form.className === 'string' &&
        form.className.indexOf('product-request-form') !== -1) return true;
    return false;
  }

  function populateHiddenFields(form) {
    PARAMS.forEach(function (param) {
      var fieldName = 'contact[' + param + ']';
      var field = form.querySelector('[name="' + fieldName + '"]');
      if (field) {
        var val = getCookie(COOKIE_PREFIX + param) || '';
        if (val && !field.value) field.value = val;
      }
    });
    ['landing_page', 'referrer_url'].forEach(function (key) {
      var field = form.querySelector('[name="contact[' + key + ']"]');
      if (field && !field.value) {
        field.value = getCookie(COOKIE_PREFIX + key) || '';
      }
    });
  }

  function fireContactSubmitEvent(form) {
    var gclidValue = getCookie(COOKIE_PREFIX + 'gclid') || '';
    var formId = form.id || 'contact-us-native';
    if (typeof gtag === 'function') {
      gtag('event', 'form_submit', {
        form_id: formId,
        form_name: 'Contact Us',
        form_destination: FORM_DESTINATION,
        value: CONTACT_FORM_VALUE,
        currency: 'USD',
        gclid: gclidValue,
        click_id: gclidValue
      });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form_submit',
      form_id: formId,
      form_name: 'Contact Us',
      form_destination: FORM_DESTINATION,
      value: CONTACT_FORM_VALUE,
      currency: 'USD',
      alma_gclid: gclidValue,
      click_id: gclidValue
    });
  }

  function fireProductInquiryEvent(form) {
    var gclidValue = getCookie(COOKIE_PREFIX + 'gclid') || '';
    var productTitle = (form.querySelector('input[name="contact[product]"]') || {}).value || '';
    var sku = (form.querySelector('input[name="contact[sku]"]') || {}).value || '';
    var formId = form.id || 'product-request-form';
    if (typeof gtag === 'function') {
      gtag('event', 'product_inquiry', {
        form_id: formId,
        form_name: 'Product Inquiry',
        form_destination: FORM_DESTINATION,
        product_title: productTitle,
        sku: sku,
        value: PRODUCT_INQUIRY_VALUE,
        currency: 'USD',
        gclid: gclidValue,
        click_id: gclidValue
      });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'product_inquiry',
      form_id: formId,
      form_name: 'Product Inquiry',
      form_destination: FORM_DESTINATION,
      product_title: productTitle,
      sku: sku,
      value: PRODUCT_INQUIRY_VALUE,
      currency: 'USD',
      alma_gclid: gclidValue,
      click_id: gclidValue
    });
  }

  function attachSubmitListener(form) {
    if (form.dataset.almaListenerAttached === '1') return;
    form.dataset.almaListenerAttached = '1';
    var fireEvent = isProductInquiryForm(form)
      ? fireProductInquiryEvent
      : fireContactSubmitEvent;
    form.addEventListener('submit', function () {
      fireEvent(form);
    });
  }

  function processAllForms() {
    var forms = Array.from(document.querySelectorAll('form'))
      .filter(function (f) {
        return f.querySelector('input[name="contact[gclid]"]');
      });
    forms.forEach(function (form) {
      populateHiddenFields(form);
      attachSubmitListener(form);
    });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  // Also clear any stale SHOP-12 pending data left over from the
  // 2026-05-05 attempt — defensive cleanup so it doesn't linger and
  // someone debugs an empty-key error later.
  try { sessionStorage.removeItem('alma_pending_form_event'); } catch (e) {}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAllForms);
  } else {
    processAllForms();
  }

})();
